import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Search, ChevronRight, UserPlus, Trash2, X, AlertCircle, Building2 } from 'lucide-react'
import { isAxiosError } from 'axios'
import {
  updateAdminOrganisation,
  addAdminOrgMember,
  removeAdminOrgMember,
} from '../../services/api'
import { useAuthedFetch } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'

interface AdminOrgSummary { org_id: string; name: string; organisation_type?: string; owner_id?: string; member_count: number }
interface AdminOrgDetail {
  organisation: { org_id: string; name: string; organisation_type?: string; owner_id?: string }
  members: { user_id: string; email: string; role: string }[]
}
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import HelpPopover from '../../components/help/HelpPopover'

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

const ORG_TYPES = [
  { value: '', label: 'Not set' },
  { value: 'vc_fund', label: 'VC / Fund' },
  { value: 'ecosystem_convener', label: 'Ecosystem / Convener' },
  { value: 'corporate_industry', label: 'Corporate / Industry' },
  { value: 'research_institution', label: 'Research Institution' },
  { value: 'government_agency', label: 'Government Agency' },
  { value: 'accelerator_incubator', label: 'Accelerator / Incubator' },
]

function orgTypeLabel(type?: string) {
  return ORG_TYPES.find((t) => t.value === (type ?? ''))?.label ?? 'Not set'
}

interface OrgMemberRef { user_id: string; email?: string }

export default function OrganisationsManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [showAddMember, setShowAddMember] = useState(false)
  const [newMemberEmail, setNewMemberEmail] = useState('')
  const [newMemberRole, setNewMemberRole] = useState('member')
  const [pendingTypeChanges, setPendingTypeChanges] = useState<Record<string, string>>({})
  const [savingOrg, setSavingOrg] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [addMemberError, setAddMemberError] = useState<string | null>(null)
  const [removeMemberTarget, setRemoveMemberTarget] = useState<OrgMemberRef | null>(null)

  // Direct-fetch path (see hooks/useAuthedFetch) because the axios read hangs
  // inside the /admin route context.
  const { data: orgsData, isLoading, refetch: refetchOrgs } =
    useAuthedFetch<{ organisations: AdminOrgSummary[]; count: number }>('/admin/organisations')

  const { data: orgDetail, isLoading: detailLoading, refetch: refetchDetail } =
    useAuthedFetch<AdminOrgDetail>(selectedOrgId ? `/admin/organisations/${selectedOrgId}` : null)

  const updateOrgMutation = useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: { name?: string; organisation_type?: string } }) =>
      updateAdminOrganisation(orgId, data),
    onSuccess: (_data, { orgId }) => {
      refetchOrgs()
      if (orgId === selectedOrgId) refetchDetail()
      setPendingTypeChanges((prev) => { const next = { ...prev }; delete next[orgId]; return next })
      setSavingOrg(null)
      setActionError(null)
    },
    // BUG-121: report save failures instead of just clearing the spinner.
    onError: (err) => {
      setSavingOrg(null)
      setActionError(errorMessage(err, 'Failed to update the organisation type. Please try again.'))
    },
  })

  const addMemberMutation = useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: string }) =>
      addAdminOrgMember(orgId, { user_id: userId, role }),
    onSuccess: () => {
      refetchDetail()
      refetchOrgs()
      setShowAddMember(false)
      setNewMemberEmail('')
      setNewMemberRole('member')
      setAddMemberError(null)
    },
    // BUG-121: surface add-member failures in the modal.
    onError: (err) => setAddMemberError(errorMessage(err, 'Failed to add the member. Please try again.')),
  })

  const orgs = orgsData?.organisations ?? []
  const filtered = orgs.filter(
    (o) =>
      o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      orgTypeLabel(o.organisation_type).toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleTypeChange = (orgId: string, newType: string) => {
    setPendingTypeChanges((prev) => ({ ...prev, [orgId]: newType }))
  }

  const handleSaveType = async (orgId: string) => {
    const newType = pendingTypeChanges[orgId]
    if (newType === undefined) return
    setSavingOrg(orgId)
    setActionError(null)
    // BUG-150: send the value directly — including '' when clearing to "Not set".
    // axios drops `undefined` body fields, so the previous `newType || undefined`
    // sent an empty PATCH body and the reset silently no-op'd. An explicit empty
    // string is a real value the backend accepts to clear the org type.
    updateOrgMutation.mutate({ orgId, data: { organisation_type: newType } })
  }

  const selectedOrg = orgDetail?.organisation
  const members = orgDetail?.members ?? []

  const loadTimedOut = useTimedOut(isLoading)
  if (isLoading && !loadTimedOut) return <LoadingSkeleton rows={5} type="table" />

  return (
    <div>
      <PageHeader
        title="Organisations"
        description="Every organisation on the platform. Set its type and manage its members — InnovateFlow administrators only."
        status={{ label: `${orgs.length} organisation${orgs.length === 1 ? '' : 's'}`, tone: 'neutral' }}
        secondaryActions={
          <HelpPopover
            topic="admin-organisations"
            title="Organisations"
            body="Every organisation registered on the platform. Classify each by type and add or remove its members. Changes here are platform-wide and audited."
            size={18}
          />
        }
      />

      <div className="mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search organisations..."
            className="w-full pl-9 pr-3 py-2 border border-sky-200 rounded-lg text-sm focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white/80"
          />
        </div>
      </div>

      {/* BUG-121: page-level mutation error banner (save type / remove member) */}
      {actionError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={orgs.length === 0 ? 'No organisations yet' : 'No matching organisations'}
          description={
            orgs.length === 0
              ? 'Organisations appear here once they are registered on the platform.'
              : 'No organisations match your search. Try a different name or type.'
          }
        />
      ) : (
        <div className="light-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-sky-500/5 border-b border-sky-200/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Organisation</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Members</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sky-200/30">
              {filtered.map((org) => {
                const pendingType = pendingTypeChanges[org.org_id]
                const displayType = pendingType !== undefined ? pendingType : (org.organisation_type ?? '')
                const isDirty = pendingType !== undefined && pendingType !== (org.organisation_type ?? '')
                const isSaving = savingOrg === org.org_id

                return (
                  <tr key={org.org_id} className="hover:bg-white/30">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-800">{org.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{org.org_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={displayType}
                        onChange={(e) => handleTypeChange(org.org_id, e.target.value)}
                        className="text-xs text-gray-800 border border-sky-200 rounded px-2 py-1 focus:ring-1 focus:ring-iflow-500 outline-none bg-white"
                      >
                        {ORG_TYPES.map((t) => (
                          <option key={t.value} className="bg-white text-gray-800" value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      {isDirty && (
                        <button
                          onClick={() => handleSaveType(org.org_id)}
                          disabled={isSaving}
                          className="ml-2 text-xs font-medium text-iflow-600 hover:text-iflow-700 disabled:opacity-50"
                        >
                          {isSaving ? 'Saving…' : 'Save'}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{org.member_count}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedOrgId(org.org_id === selectedOrgId ? null : org.org_id)}
                        className="flex items-center gap-1 ml-auto text-xs font-medium text-iflow-600 hover:text-iflow-700"
                      >
                        Members <ChevronRight size={14} className={`transition-transform ${selectedOrgId === org.org_id ? 'rotate-90' : ''}`} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Member detail panel */}
      {selectedOrgId && (
        <div className="mt-6 light-card overflow-hidden">
          <div className="bg-sky-500/5 border-b border-sky-200/50 px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {selectedOrg?.name ?? selectedOrgId} — Members
              </p>
              {selectedOrg?.organisation_type && (
                <p className="text-xs text-gray-500">{orgTypeLabel(selectedOrg.organisation_type)}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setAddMemberError(null); setShowAddMember(true) }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-iflow-700 text-white text-xs font-medium rounded-lg hover:bg-iflow-800"
              >
                <UserPlus size={14} /> Add member
              </button>
              <button
                onClick={() => setSelectedOrgId(null)}
                className="p-1.5 text-gray-500 hover:text-gray-600 rounded"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {detailLoading ? (
            <div className="p-6"><LoadingSkeleton rows={3} type="table" /></div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-sky-200/30">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Email / User ID</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sky-200/30">
                {members.map((m) => (
                  <tr key={m.user_id} className="hover:bg-white/20">
                    <td className="px-4 py-2.5">
                      <p className="text-sm text-gray-800">{m.email || m.user_id}</p>
                      {m.email && <p className="text-xs text-gray-500 font-mono">{m.user_id}</p>}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{m.role}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => setRemoveMemberTarget({ user_id: m.user_id, email: m.email })}
                        className="p-1 text-red-400 hover:text-red-600"
                        aria-label={`Remove ${m.email || m.user_id}`}
                        title="Remove member"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {members.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                      No members yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add Member Modal — light theme (P: app theme, never dark) */}
      {showAddMember && selectedOrgId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddMember(false)} />
          <div className="relative bg-white rounded-xl border border-sky-200 shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-iflow-900 mb-1">Add member</h3>
            <p className="text-sm text-gray-500 mb-4">{selectedOrg?.name}</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User ID / email (Cognito username)</label>
                <input
                  type="text"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="user@example.com or cognito-uuid"
                  className="w-full px-3 py-2 border border-sky-200 rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                  className="w-full px-3 py-2 border border-sky-200 rounded-lg text-sm text-gray-800 focus:ring-2 focus:ring-iflow-500 focus:border-iflow-500 outline-none bg-white"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              {/* BUG-121: add-member failure feedback inside the modal */}
              {addMemberError && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">{addMemberError}</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAddMember(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  addMemberMutation.mutate({
                    orgId: selectedOrgId,
                    userId: newMemberEmail.trim(),
                    role: newMemberRole,
                  })
                }
                disabled={!newMemberEmail.trim() || addMemberMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {addMemberMutation.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove member confirmation — real mutation, honest error, no fake success */}
      <ConfirmDialog
        open={!!removeMemberTarget}
        title="Remove member"
        tone="danger"
        confirmLabel="Remove member"
        message={
          removeMemberTarget
            ? `Remove ${removeMemberTarget.email || removeMemberTarget.user_id} from ${selectedOrg?.name ?? 'this organisation'}? They lose organisation access immediately.`
            : ''
        }
        onConfirm={async () => {
          if (!selectedOrgId || !removeMemberTarget) return
          await removeAdminOrgMember(selectedOrgId, removeMemberTarget.user_id)
          refetchDetail()
          refetchOrgs()
          setActionError(null)
        }}
        onClose={() => setRemoveMemberTarget(null)}
      />
    </div>
  )
}
